/**
 * ч.1346 (рішення власника, зразок — «Виписка банку» 1С): «Зведення банку» і «Зведення
 * каси» — один день за одним рахунком (касою): залишок на початок дня, усі документи дня
 * з приходом / витратою, контрагентом і призначенням, непроведені документи дня окремими
 * приглушеними рядками (у підсумки не входять), залишок на кінець дня.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { moneySummary, type MoneyDayRow } from '@/api/moneySummary';
import DatePopover from '@/components/DatePopover';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { PostedHeaderIcon, PostedIcon } from '@/shared/components/StatusIcons';
import { DOC_ROUTES } from '@/shared/utils/docRoutes';
import { pickDefaultOrg, useOrgFilter } from '@/store/defaults';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (v: string) => (Number(v) === 0 ? '' : fmt2(v));

function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function shiftDay(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, (d ?? 1) + days);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
}

export default function MoneyDaySummaryPage({ kind }: { kind: 'bank' | 'cash' }) {
  const navigate = useNavigate();
  const isBank = kind === 'bank';
  const [orgFilter, setOrgFilter] = useOrgFilter();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Зведення — завжди по одній організації: «усі» з шапки → організація за замовчуванням.
  const orgId = orgFilter || (pickDefaultOrg(orgList.data?.items)?.id ?? '');

  const [accountId, setAccountId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [onlyPosted, setOnlyPosted] = useState(false);

  const bankAccountsQ = useQuery({
    queryKey: ['bank-accounts', orgId],
    queryFn: () => refs.bankAccounts(orgId),
    enabled: isBank && !!orgId,
  });
  const cashRegistersQ = useQuery({
    queryKey: ['cash-registers', orgId],
    queryFn: () => refs.cashRegisters(orgId),
    enabled: !isBank && !!orgId,
  });
  const accounts = useMemo(() => (isBank
    ? (bankAccountsQ.data ?? []).map((a) => ({ id: a.id, label: a.account_name || a.iban, main: a.is_main }))
    : (cashRegistersQ.data ?? []).map((c) => ({ id: c.id, label: c.name, main: false }))
  ), [isBank, bankAccountsQ.data, cashRegistersQ.data]);

  // Рахунок (каса) за замовчуванням — основний або перший; при зміні організації — заново.
  useEffect(() => {
    if (accounts.length === 0) { setAccountId(''); return; }
    if (!accounts.some((a) => a.id === accountId)) {
      setAccountId((accounts.find((a) => a.main) ?? accounts[0]!).id);
    }
  }, [accounts, accountId]);

  const summaryQ = useQuery({
    queryKey: ['money-summary', kind, orgId, accountId, date],
    queryFn: () => (isBank
      ? moneySummary.bank({ organization_id: orgId, bank_account_id: accountId, date })
      : moneySummary.cash({ organization_id: orgId, cash_register_id: accountId, date })),
    enabled: !!orgId && !!accountId && !!date,
  });
  const data = summaryQ.data;
  const rows = useMemo(
    () => (data?.rows ?? []).filter((r) => !onlyPosted || r.posted),
    [data, onlyPosted],
  );

  const open = (r: MoneyDayRow) => {
    const to = DOC_ROUTES[r.document_type]?.(r.document_id);
    if (to) navigate(to);
  };

  const cols = useMemo<ColumnDef<MoneyDayRow>[]>(() => [
    {
      key: 'posted', header: <PostedHeaderIcon />, width: 40, align: 'center',
      cell: (r) => <PostedIcon posted={r.posted} note={r.posted ? null : 'не проведено — у підсумки не входить'} />,
      sortValue: (r) => (r.posted ? 1 : 0),
    },
    {
      key: 'document', header: 'Документ', width: 300,
      cell: (r) => (
        <span>
          {r.kind_label} <b>№ {r.document_number || '—'}</b>
          {r.document_time && (
            <span style={{ color: 'var(--muted)', marginLeft: 6 }}>
              {new Date(r.document_time).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </span>
      ),
      sortValue: (r) => `${r.document_time ?? ''} ${r.document_number}`,
    },
    { key: 'external_number', header: 'Номер банку', width: 120, cell: (r) => r.external_number ?? '' },
    { key: 'party', header: 'Контрагент', width: 240, cell: (r) => r.party ?? '', sortValue: (r) => r.party ?? '' },
    {
      key: 'income', header: 'Прихід', width: 130, align: 'right',
      cell: (r) => <span style={{ color: r.posted ? undefined : 'var(--muted)' }}>{money(r.income)}</span>,
      sortValue: (r) => Number(r.income),
    },
    {
      key: 'expense', header: 'Витрата', width: 130, align: 'right',
      cell: (r) => <span style={{ color: r.posted ? undefined : 'var(--muted)' }}>{money(r.expense)}</span>,
      sortValue: (r) => Number(r.expense),
    },
    { key: 'purpose', header: 'Призначення платежу', width: 320, cell: (r) => r.purpose ?? '' },
    { key: 'comment', header: 'Коментар', width: 220, cell: (r) => r.comment ?? '' },
  ], []);

  const title = isBank ? 'Зведення банку' : 'Зведення каси';
  const accountTitle = isBank ? 'Банківський рахунок' : 'Каса';
  const totals = [
    ['Залишок на початок дня', data?.opening],
    ['Прихід', data?.income],
    ['Витрата', data?.expense],
    ['Залишок на кінець дня', data?.closing],
  ] as const;

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>{title}</h2>
        {data && (
          <span style={{ color: 'var(--muted)', alignSelf: 'center' }}>
            {data.account_label}{data.currency_code && data.currency_code !== 'UAH' ? ` · ${data.currency_code}` : ''}
          </span>
        )}
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgFilter(e.target.value)} style={{ maxWidth: 260 }} aria-label="Організація">
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <select value={accountId} onChange={(e) => setAccountId(e.target.value)} style={{ maxWidth: 260 }} aria-label={accountTitle}>
          {accounts.length === 0 && <option value="">— немає —</option>}
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button type="button" onClick={() => setDate(shiftDay(date, -1))} title="попередній день" aria-label="попередній день">‹</button>
          <DatePopover value={date} onChange={setDate} />
          <button type="button" onClick={() => setDate(shiftDay(date, 1))} title="наступний день" aria-label="наступний день">›</button>
          <button type="button" onClick={() => setDate(todayIso())} disabled={date === todayIso()}>сьогодні</button>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={onlyPosted} onChange={(e) => setOnlyPosted(e.target.checked)} />
          лише проведені
        </label>
      </div>

      {summaryQ.isError && (
        <div className="error" style={{ marginBottom: '0.6rem' }}>Не вдалося побудувати зведення. Перевірте вибір рахунку і дату.</div>
      )}

      <DataTable<MoneyDayRow>
        rows={rows}
        columns={cols}
        rowKey={(r) => `${r.document_type}:${r.document_id}`}
        loading={summaryQ.isLoading}
        emptyText={accountId ? 'За цей день документів немає' : (isBank ? 'Додайте банківський рахунок організації' : 'Додайте касу організації')}
        onRowDoubleClick={open}
        rowMuted={(r) => (r.posted ? undefined : 0.55)}
        storageKey={`money-summary-${kind}`}
        initialSortKey="document"
        initialSortDir="asc"
      />

      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '0.8rem', fontSize: '0.9rem' }}>
        {totals.map(([label, value]) => (
          <div key={label} style={{ textAlign: 'right' }}>
            <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>{label}</div>
            <div style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value != null ? fmt2(value) : '—'}</div>
          </div>
        ))}
      </div>
      {data && data.unposted_count > 0 && !onlyPosted && (
        <div style={{ color: 'var(--muted)', fontSize: '0.8rem', marginTop: 6 }}>
          Непроведених документів за день: {data.unposted_count} — показані приглушено, у підсумки не входять.
        </div>
      )}
      {data?.ledger_note && (
        <div style={{ color: 'var(--badge-amber-fg)', fontSize: '0.8rem', marginTop: 6 }}>{data.ledger_note}</div>
      )}
    </div>
  );
}
